import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import { Drawer } from 'antd';
import { Loading3QuartersOutlined } from '@ant-design/icons';
import { useInterval, useTimeout } from 'ahooks';
import { Icon } from '@fuxi/eevee-icon';
import service from '@/service';
import { storagePrefixV2 } from '@/constant';
import FormatTime from '../format-time';
import OrderFromWechat from '../order-from-wechat';
import cx from './index.module.scss';

interface Props {
  isDrawerOpen: boolean;
  setIsDrawerOpen: Dispatch<SetStateAction<boolean>>;
  setLoadingRemainTime: React.Dispatch<React.SetStateAction<number>>;
  afterClose: () => void;
  onVerifyWxOrder: VoidFunc;
  onOrderEndOfTimeout: VoidFunc;
}
const timeoutImg = storagePrefixV2 + '/time-out.png';
const WriteOffOrder: React.FC<Props> = ({
  isDrawerOpen,
  setIsDrawerOpen,
  setLoadingRemainTime,
  afterClose,
  onVerifyWxOrder,
  onOrderEndOfTimeout,
}) => {
  const [isLoadingQRCode, setIsLoadingQRCode] = useState(false);
  const [QRCodeInfo, setQRCodeInfo] = useState<GetQRCodeResponse>(null);
  const [isConfirmedPlan, setIsConfirmedPlan] = useState(false);
  const [QRCodeState, setQRCodeState] = useState<GetQRCodeStateResponse['state']>('New');
  const [hasLoopTimeout, setHasLoopTimeout] = useState(false);
  const [orderTimeoutCount, setOrderTimeoutCount] = useState(undefined);
  const [orderFromWXInfo, setOrderFromWXInfo] = useState<GetOrderFromWxResponse>({} as GetOrderFromWxResponse);
  const [QRCodeLoopTime, setQRCodeLoopTime] = useState(null);

  // 获取二维码的轮询
  const clearQRLoop = useInterval(
    () => {
      getQRCode();
    },
    QRCodeLoopTime,
    {
      immediate: true,
    }
  );

  // 检测二维码状态的轮询
  const clearQRStateLoop = useInterval(
    () => {
      getQRCodeState(QRCodeInfo?.key);
    },
    QRCodeInfo?.key ? 4 * 1000 : null,
    {
      immediate: true,
    }
  );

  // 获取来自微信的订单的轮询
  const clearGetOrderFromWXLoop = useInterval(
    () => {
      getOrderFromWx();
    },
    QRCodeState === 'Used' ? 4 * 1000 : null
  );

  // 订单状态变成Used之后超过一分钟，结束循环，并将状态设置为超时
  useTimeout(() => {
    setHasLoopTimeout(true);
    handleClearAllInterval();
  }, orderTimeoutCount);

  /** 清除所有的轮询计时器 */
  const handleClearAllInterval = useCallback(() => {
    clearQRLoop();
    clearQRStateLoop();
    clearGetOrderFromWXLoop();
  }, [clearGetOrderFromWXLoop, clearQRLoop, clearQRStateLoop]);

  /** 初始化获取订单状态，如果有订单则回显，没有订单则获取二维码 */
  const getPreviousOrder = useCallback(async () => {
    const { data } = await service.service.getCreatedWxOrder();
    // 有订单数据就回显，否则获取二维码
    if (data?.payOrderId) {
      setOrderFromWXInfo(data);
      setIsConfirmedPlan(true);
    } else {
      setQRCodeLoopTime(5 * 60 * 1000);
    }
  }, []);

  const getQRCode = useCallback(async () => {
    try {
      setIsLoadingQRCode(true);
      const { data } = await service.device.getQRCode();
      setQRCodeInfo(data);
    } finally {
      setIsLoadingQRCode(false);
    }
  }, []);

  const getQRCodeState = useCallback(
    async key => {
      const { data } = await service.device.getQRCodeState(key);
      setQRCodeState(data?.state);
      if (data?.state === 'Expired') {
        handleClearAllInterval();
      } else if (data.state === 'Used') {
        clearQRLoop();
        clearQRStateLoop();
        setOrderTimeoutCount(60 * 1000);
        setLoadingRemainTime(60 * 1000);
      } else {
        setLoadingRemainTime(undefined);
      }
    },
    [clearQRLoop, clearQRStateLoop, handleClearAllInterval, setLoadingRemainTime]
  );

  const getOrderFromWx = useCallback(async () => {
    const { data } = await service.service.getOrderFromWx();
    setOrderFromWXInfo(data);
    if (data) {
      handleClearAllInterval();
    }
  }, [handleClearAllInterval]);

  useEffect(() => {
    getPreviousOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cx('write-off-order')}>
      <Drawer
        placement="right"
        width="520px"
        className={cx('write-off-order-drawer')}
        closeIcon={<Icon name="close-drawer" onClick={() => setIsDrawerOpen(false)} size={28} />}
        styles={{ header: { border: 'none' } }}
        extra={<FormatTime />}
        afterOpenChange={open => {
          if (!open) {
            afterClose?.();
          }
        }}
        open={isDrawerOpen}
      >
        {orderFromWXInfo?.payOrderId ? (
          <OrderFromWechat
            onVerifyWxOrder={onVerifyWxOrder}
            onOrderEndOfTimeout={onOrderEndOfTimeout}
            orderInfo={orderFromWXInfo}
            isConfirmedPlan={isConfirmedPlan}
            setIsConfirmedPlan={setIsConfirmedPlan}
            setIsDrawerOpen={setIsDrawerOpen}
          />
        ) : (
          <div className={cx('before-order')}>
            {hasLoopTimeout || QRCodeState === 'Expired' ? (
              <div className={cx('timeout')}>
                <img src={timeoutImg} alt="code-img" />
                <div className={cx('timeout-text')}>订单超时</div>
              </div>
            ) : QRCodeState === 'New' ? (
              <div className={cx('code')}>
                {isLoadingQRCode ? (
                  <Loading3QuartersOutlined className={cx('loading-icon')} spin />
                ) : (
                  QRCodeInfo?.qrCode && <img src={`data:image/jpeg;base64,${QRCodeInfo?.qrCode}`} alt="code-img" />
                )}
                <div className={cx('tip-text')}>微信扫码</div>
              </div>
            ) : (
              <div className={cx('loading')}>
                <Loading3QuartersOutlined spin className={cx('icon')} />
                <div className={cx('loading-text')}>顾客下单中，请稍候...</div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default WriteOffOrder;
